import * as CL from '@design-system/component-library';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses';
import { pcsMsg, t } from '../../common/i18n';
import { Grid } from '../Grid/Grid.js';
import { LinkableAccordion } from '../LinkableAccordion';
import type { DeviceSelection } from './CatalogProductSelectionFormWrapper';
import type { DiscontinuedProduct } from '../../generated/api/discontinuedProduct';

interface DiscontinuedDevicesAccordionProps {
  discontinuedDevices: DiscontinuedProduct[];
}

interface DiscontinuedProductItemProps {
  onRemove: () => void;
}

const DiscontinuedDevice = ({ onRemove }: DiscontinuedProductItemProps) => (
  <CL.Button
    className={`${dsClass.PADDING_LEFT_2} ${dsClass.PADDING_RIGHT_2}`}
    color="link"
    onClick={onRemove}
    icon={<CL.Icon icon="delete" color="primary" />}
    iconPosition="right"
    size="l"
  >
    {t.X05S('Remove from the list')}
  </CL.Button>
);

export const DiscontinuedDevicesAccordion = ({ discontinuedDevices }: DiscontinuedDevicesAccordionProps) => {
  const { setValue, watch } = useFormContext<DeviceSelection>();
  const removedDiscontinuedDevices = watch('removedDiscontinuedDevices') || [];

  const visibleDiscontinuedDevices = discontinuedDevices.filter(
    device => !removedDiscontinuedDevices.includes(device.onlineModelCode)
  );

  if (visibleDiscontinuedDevices.length === 0) {
    return null;
  }

  const onRemove = async (onlineModelCode: string) =>
    setValue('removedDiscontinuedDevices', [...removedDiscontinuedDevices, onlineModelCode]);

  return (
    <Grid colWidthL={10}>
      <LinkableAccordion
        heading={t.GL4B('Products removed from selection')}
        headingLevel="h3"
        id="additional-services"
        badge={`${visibleDiscontinuedDevices.length} ${t.B3MG(pcsMsg)}`}
        badgeColor="orange"
      >
        <div className={`${dsClass.PADDING_BOTTOM_0} of-catalog-products-selection__discontinued-devices`}>
          {visibleDiscontinuedDevices.map(device => (
            <div
              className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_SPACE_BETWEEN}`}
              key={device.onlineModelName}
            >
              <div className={dsClass.ALIGN_CONTENT_CENTER}>{device.onlineModelName}</div>
              <div className={dsClass.ALIGN_CONTENT_CENTER}>
                <DiscontinuedDevice onRemove={() => onRemove(device.onlineModelCode)} />
              </div>
            </div>
          ))}
        </div>
      </LinkableAccordion>
    </Grid>
  );
};
